import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLinkIcon } from "lucide-react";
import { useState } from "react";
import { CgSoftwareDownload } from "react-icons/cg";
import { MdLink } from "react-icons/md";
import { PiMagicWandDuotone } from "react-icons/pi";
import styled from "styled-components";
import { Button } from "@/components/ui/button";
import ThemedSkeleton from "../../ui/ThemedSkeleton";
import { useGetContractAttachments } from "./useGetContractAttachments";
import { useSummarizeAttachment } from "./useSummarizeAttachment";
import Markdown from "react-markdown";
import {
  FaFile,
  FaFileExcel,
  FaFileImage,
  FaFilePdf,
  FaFileWord,
} from "react-icons/fa";
import { ChevronRight } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useParams } from "react-router-dom";
import { useFileType } from "./useFileType";

const StyledAttachment = styled.div`
  transition: all 0.3s;
  display: inline-flex;
  color: var(--gray-700);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);
  padding: 0.75rem /* 20px */;
  margin-top: 0.5rem /* 8px */;
  margin-bottom: 0.5rem /* 8px */;
  width: 100%;
  align-items: center;
  &:hover {
    color: var(--gray-900);
    box-shadow: var(--shadow-md);
  }
`;

// helper component
function TooltipIconButton({ children, title, tooltip, icon: Icon, ...props }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button {...props}>
          {Icon && <Icon />}
          <span>{title}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}

const extensionToIconMap = {
  ".pdf": (
    <FaFilePdf className="h-6 w-6 self-center text-[var(--red-500)] rounded-md" />
  ),
  ".docx": (
    <FaFileWord className="h-6 w-6 self-center text-[var(--blue-500)] rounded-md" />
  ),
  ".xlsx": (
    <FaFileExcel className="h-6 w-6 self-center text-[var(--green-500)] rounded-md" />
  ),
  ".jpg": (
    <FaFileImage className="h-6 w-6 self-center text-[var(--purple-500)] rounded-md" />
  ),
  ".png": (
    <FaFileImage className="h-6 w-6 self-center text-[var(--purple-500)] rounded-md" />
  ),
  default: (
    <FaFile className="h-6 w-6 self-center text-[var(--gray-500)] rounded-md" />
  ),
};

function Attachment({ attachment, searchType, i, ...props }) {
  const queryClient = useQueryClient();

  const [summary, setSummary] = useState(
    queryClient.getQueryData([
      "attachmentSummary",
      searchType,
      attachment.attachmentId,
    ])
  );

  const { mutate, isLoading } = useSummarizeAttachment(
    attachment.attachmentId,
    searchType
  );

  const summarize = (fileLink, extension) => {
    mutate(
      { file_link: fileLink, extension },
      {
        onSuccess: (result) => {
          setSummary(result);
        },
      }
    );
  };

  const name = attachment.name;
  const extension = attachment.extension;
  const icon = extensionToIconMap[extension] || extensionToIconMap.default;

  return (
    <StyledAttachment {...props}>
      <div className="flex flex-col gap-0 w-full">
        <div className="flex justify-between">
          <div className="flex gap-1 items-center">
            {attachment.type === "link" ? (
              <MdLink />
            ) : (
              <div className="rounded-sm p-1 mr-4">{icon}</div>
            )}
            {name || `Attachment ${i}:  No name found.`}
          </div>
          {/* {JSON.stringify(actions, null, 2)} */}
          <div className="flex gap-2">
            <TooltipProvider delayDuration={300}>
              {attachment.type === "file" && (
                <TooltipIconButton
                  title="Summarize"
                  tooltip="Get an AI summary of this document"
                  className={"text-xs [&_svg]:text-secondary-foreground"}
                  variant="outline"
                  icon={PiMagicWandDuotone}
                  onClick={() => summarize(attachment.fileLink, extension)}
                  disabled={
                    isLoading ||
                    !([".pdf", ".doc", ".docx"].indexOf(extension) >= 0) ||
                      (
                        searchType === "federal" &&
                        (attachment?.fileExists !== "1" ||
                         attachment?.accessLevel !== "public" ||
                         attachment?.size > 1e7)
                      )
                  }
                />
              )}
              {attachment.type === "file" ? (
                <TooltipIconButton
                  title="Download"
                  tooltip="Download attachment"
                  className={"text-xs [&_svg]:text-secondary-foreground"}
                  variant="outline"
                  icon={CgSoftwareDownload}
                  onClick={() => window.open(attachment.fileLink)}
                  disabled={
                    searchType === "federal" &&
                    (attachment?.fileExists !== "1" ||
                      attachment?.accessLevel !== "public")
                  }
                />
              ) : (
                <TooltipIconButton
                  title="View"
                  tooltip={"View external link"}
                  className={"text-xs [&_svg]:text-secondary-foreground"}
                  variant="outline"
                  icon={ExternalLinkIcon}
                  onClick={() =>
                    attachment.link &&
                    window.open(
                      attachment.link,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                  disabled={isLoading || !attachment.link}
                />
              )}
            </TooltipProvider>
          </div>
        </div>
        {summary && (
          <Collapsible defaultOpen={true} className="group/collapsible2">
            <CollapsibleTrigger asChild>
              <Button
                variant="link"
                className="w-32 justify-between text-background-foreground "
              >
                <span className="hidden group-data-[state=closed]/collapsible2:inline">
                  Show Summary
                </span>
                <span className="hidden group-data-[state=open]/collapsible2:inline">
                  Hide Summary
                </span>
                <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible2:rotate-90" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-wrap gap-2">
              <Separator className="mt-1 mb-4 bg-muted" />
              <Markdown className=" text-muted-foreground">{summary}</Markdown>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </StyledAttachment>
  );
}

function SkeletonDescription({ detailsCount }) {
  const detailsArray = Array.from(Array(detailsCount).keys());
  return (
    <>
      {detailsArray.map((child, i) => (
        <div key={i}>
          <ThemedSkeleton />
        </div>
      ))}
    </>
  );
}

export function getAttachment(attachment, searchType) {
  const actions = {
    federal: {
      name: attachment?.name || attachment?.fileName || attachment?.description,
      attachmentId: attachment?.resourceId,
      fileLink: `https://sam.gov/api/prod/opps/v3/opportunities/resources/files/${attachment.resourceId}/download`,
      link: attachment?.uri,
      type: attachment?.type,
      extension: attachment?.mimeType,
    },
    sled: {
      name: attachment?.name || attachment?.fileName || attachment?.description,

      attachmentId: attachment.id,
      fileLink: attachment?.link,
      link: attachment?.link,
      type: "file",
    },
    grant: {
      name: attachment?.name || attachment?.fileName || attachment?.description,

      attachmentId: String(attachment.id),
      fileLink: `https://apply07.grants.gov/grantsws/rest/opportunity/att/download/${attachment.id}`,
      link: "",
      type: "file",
    },
    sbir: {
      name: attachment?.name || attachment?.fileName || attachment?.description,

      attachmentId: String(attachment.id),
      fileLink: "",
      link: attachment?.link,
      type: "link",
    },
  };
  return { ...attachment, ...actions[searchType] };
}

function Attachments({ attachments, isLoading, searchType }) {
  const { proposal_id } = useParams();
  const files = attachments?.map((attachment) =>
    getAttachment(attachment, searchType)
  );

  const { data: extensionAttachments } = useFileType(proposal_id, files);

  if (isLoading) {
    return <SkeletonDescription />;
  }

  if (!attachments || attachments.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No attachments available.
      </div>
    );
  }
  return (
    <div id="contract-attachments">
      <Collapsible defaultOpen={true} className="group/collapsible">
        <CollapsibleTrigger asChild>
          <Button variant="link" className="w-full text-background-foreground ">
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {(extensionAttachments || files)?.map((attachment, i) => (
            <div className="rounded-md" key={attachment.attachmentId}>
              <Attachment
                key={attachment.attachmentId}
                attachment={attachment}
                searchType={searchType}
                i={i}
              />
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function FederalAttachments({ proposalId, searchType }) {
  const { data, isLoading } = useGetContractAttachments(searchType, proposalId);
  const attachments = data?.map((attachment) =>
    getAttachment(attachment, searchType)
  );
  if (isLoading) {
    return (
      <div className="flex flex-col space-y-4">
        <ThemedSkeleton className="w-2/3 h-8" />
        <ThemedSkeleton className="w-1/3 h-8" />
        <ThemedSkeleton className="w-1/6 h-8" />
        <ThemedSkeleton className="w-1/2 h-8" />
        <ThemedSkeleton className="w-1/3 h-8" />
      </div>
    );
  }

  if (!attachments || attachments.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No attachments available.
      </div>
    );
  }

  return (
    <div id="contract-attachments">
      <Collapsible defaultOpen={true} className="group/collapsible">
        <CollapsibleTrigger asChild>
          <Button variant="link" className="w-full text-background-foreground ">
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {attachments.map((attachment, i) => (
            <div className="rounded-md" key={attachment.attachmentId}>
              <Attachment
                attachment={attachment}
                searchType={searchType}
                i={i}
                id={attachment.resourceId}
              />
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export { Attachment, FederalAttachments, Attachments };
